import {
  Box,
  Button,
  Typography,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export type LoginWithoutAccountFormProps = {
  initialValues: {
    lastName: string;
    orderNumber: string;
    passportNoOrCitizenshipNumber: string;
  };
  onSubmit: (values: {
    lastName: string;
    queryValue: string;
    queryType: 'orderNumber' | 'passportNoOrCitizenshipNumber';
  }) => void;
  hiddenLoginButton?: boolean;
};

export function LoginWithoutAccountForm({
  initialValues,
  onSubmit,
  hiddenLoginButton,
}: LoginWithoutAccountFormProps) {
  const [values, setValues] = useState(initialValues);
  const [queryType, setQueryType] = useState<
    'orderNumber' | 'passportNoOrCitizenshipNumber'
  >('orderNumber');
  const { t } = useTranslation();
  const nav = useNavigate();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.trim();
    setValues({ ...values, [e.target.name]: newValue });
  };

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    const queryValue =
      queryType === 'orderNumber'
        ? values.orderNumber
        : values.passportNoOrCitizenshipNumber;
    onSubmit({
      lastName: values.lastName,
      queryValue,
      queryType,
    });
  };

  return (
    <form style={{ width: '100%' }} onSubmit={handleSubmit}>
      <Box display={'flex'} flexDirection={'column'}>
        <Typography
          sx={{
            display: 'flex',
            fontSize: '12px',
            textAlign: 'center',
            mx: 1,
            mb: 1,
          }}
        >
          {t('loginWithoutAccountForm.selectQueryType')}
        </Typography>
        <ToggleButtonGroup
          value={queryType}
          exclusive
          onChange={(_, newSelection) => {
            if (newSelection !== null) setQueryType(newSelection);
          }}
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <ToggleButton
            value="orderNumber"
            sx={{
              flex: 1,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ fontFamily: 'sans-serif' }}>
              {t('loginWithoutAccountForm.pnrOrOrderNumber')}
            </Typography>
          </ToggleButton>
          <ToggleButton
            value="passportNoOrCitizenshipNumber"
            sx={{
              flex: 1,
              textAlign: 'center',
              fontSize: '11px',
            }}
          >
            {t('loginWithoutAccountForm.passportNoOrCitizenshipNumber')}
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography sx={{ display: 'flex' }}>
          {t('loginWithoutAccountForm.lastName')}
        </Typography>
        <TextField
          type="text"
          name="lastName"
          value={values.lastName}
          onChange={handleInputChange}
          color="secondary"
          size="small"
          required
          sx={{
            mb: 3,
            width: '100%',
            textAlign: 'center',
            backgroundColor: '#FAFAFA',
          }}
        />

        {queryType === 'orderNumber' && (
          <>
            <Typography sx={{ display: 'flex' }}>
              {t('loginWithoutAccountForm.pnrOrOrderNumber')}
            </Typography>
            <TextField
              type="text"
              name="orderNumber"
              placeholder={t('loginWithoutAccountForm.pnrOrOrderNumber')}
              value={values.orderNumber}
              onChange={handleInputChange}
              color="secondary"
              size="small"
              required
              sx={{
                mb: 3,
                width: '100%',
                textAlign: 'center',
                backgroundColor: '#FAFAFA',
              }}
            />
          </>
        )}

        {queryType === 'passportNoOrCitizenshipNumber' && (
          <>
            <Typography sx={{ display: 'flex' }}>
              {t('loginWithoutAccountForm.passportNoOrCitizenshipNumber')}
            </Typography>
            <TextField
              type="text"
              name="passportNoOrCitizenshipNumber"
              placeholder={t(
                'loginWithoutAccountForm.passportNoOrCitizenshipNumber',
              )}
              value={values.passportNoOrCitizenshipNumber}
              onChange={handleInputChange}
              color="secondary"
              size="small"
              required
              sx={{
                mb: 3,
                width: '100%',
                textAlign: 'center',
                backgroundColor: '#FAFAFA',
              }}
            />
          </>
        )}
      </Box>
      {!hiddenLoginButton && (
        <Box sx={{ display: 'flex', justifyContent: 'end', mt: 1 }}>
          <Typography
            sx={{
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
            onClick={() => nav('/login')}
          >
            {t('loginWithoutAccountForm.userLogin')}
          </Typography>
        </Box>
      )}
      <Button
        color="secondary"
        sx={{ mt: 3, width: '100%' }}
        variant={'contained'}
        type={'submit'}
      >
        {t('loginWithoutAccountForm.submitBtn')}
      </Button>
    </form>
  );
}
